/**
 * The shared plain-language vocabulary of the permissions surface: the §14
 * view-mode ladder, the see-rows derived from the masking registry, and every
 * catalog verb translated per its enforcement site. Lives apart from the page
 * so the matrix and the role editor can both import it without cycles.
 */ import { jsx as _jsx } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
// The §14 projection ladder, strongest visibility first. One-line meanings —
// these are what the resolver actually does, not aspirations.
export const VIEW_MODE_LEGEND = [
    {
        mode: 'full',
        label: 'Full',
        meaning: 'Sees every field unmasked and may act per the permitted actions.',
        className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
    },
    {
        mode: 'simplified',
        label: 'Simplified',
        meaning: 'Sees rolled-up values; detailed financial breakdowns are hidden.',
        className: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
    },
    {
        mode: 'masked',
        label: 'Masked',
        meaning: 'Sees records exist, but sensitive fields (money, rates) render redacted.',
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
    },
    {
        mode: 'action_blocked',
        label: 'Read-only',
        meaning: 'Sees content in full but every change is refused.',
        className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200'
    },
    {
        mode: 'no_view',
        label: 'No view',
        meaning: 'The object is invisible — reads return not-found.',
        className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
    }
];
export function viewModeClass(mode) {
    return VIEW_MODE_LEGEND.find((l)=>l.mode === mode)?.className ?? 'bg-muted text-muted-foreground';
}
export function viewModeLabel(mode) {
    return VIEW_MODE_LEGEND.find((l)=>l.mode === mode)?.label ?? mode;
}
export function ViewModeBadge({ mode }) {
    return /*#__PURE__*/ _jsx(Badge, {
        variant: "secondary",
        className: `whitespace-nowrap ${viewModeClass(mode)}`,
        "data-view-mode": mode,
        children: viewModeLabel(mode)
    });
}
// ---------------------------------------------------------------------------
// "What they can see" — the sensitive-field registry (enies-lobby main.go
// sensitiveFieldsByKind) summarized into the four things it actually hides on
// Simplified/Masked views. Read-only (action_blocked) and Full carry no
// masking; no_view hides everything.
// Each row carries the sensitive-field names it summarizes (mirroring the
// enies-lobby registry) so a bundle's unmask carve-outs can light rows back
// up honestly — the matrix must never contradict the resolver.
export const SEE_ROWS = [
    {
        key: 'content',
        label: 'Program content',
        description: 'Tasks, deliverables, schedules, documents, evidence',
        fields: []
    },
    {
        key: 'money',
        label: 'Contract dollars & numbers',
        description: 'TCV, funded amount, contract & Deltek numbers',
        fields: [
            'current_tcv',
            'tcv_on_award',
            'funded_amount',
            'total_value',
            'contract_number',
            'deltek_project_number'
        ]
    },
    {
        key: 'financial_detail',
        label: 'Financial detail',
        description: 'EAC revenue/cost lines, CLIN pricing, invoicing',
        fields: [
            'revenue',
            'direct_costs',
            'subcontractor_costs'
        ]
    },
    {
        key: 'quality_scores',
        label: 'Quality scores',
        description: 'CPARS & QASP ratings, DPAF margin, DSO',
        fields: [
            'cpars_score',
            'qasp_score',
            'latest_cpars',
            'latest_qasp',
            'latest_dpaf_margin',
            'latest_dso'
        ]
    },
    {
        key: 'kpis_staffing',
        label: 'KPIs & staffing',
        description: 'On-time %, funding utilization, risk/issue counts, FTEs',
        fields: [
            'on_time_pct',
            'funding_utilization_pct',
            'evidence_completeness_pct',
            'open_risk_count',
            'open_issue_count',
            'total_ftes',
            'funded_percent'
        ]
    }
];
export function canSee(mode, rowKey, unmaskFields) {
    if (mode === 'no_view') return 'hidden';
    if (mode === 'full' || mode === 'action_blocked') return 'full';
    if (rowKey === 'content') return 'full';
    const row = SEE_ROWS.find((r)=>r.key === rowKey);
    if (!row || !unmaskFields?.length) return 'hidden';
    const unmasked = row.fields.filter((f)=>unmaskFields.includes(f)).length;
    if (unmasked === 0) return 'hidden';
    return unmasked === row.fields.length ? 'full' : 'partial';
}
export const CAPABILITY_GROUPS = [
    {
        group: 'Everyday work',
        items: [
            {
                action: 'update',
                label: 'Edit program content',
                description: 'Upload deliverables, record deliveries, edit tasks, risks, issues, records'
            },
            {
                action: 'create',
                label: 'Create new records',
                description: 'Add risks, issues, financial entries, tracking items'
            },
            {
                action: 'export',
                label: 'Export & download',
                description: 'Export data and download documents'
            }
        ]
    },
    {
        group: 'Approvals & lifecycle',
        items: [
            {
                action: 'release_library_item',
                label: 'Release & withdraw Library items',
                description: "Release prepared Library content to an internal audience or withdraw a release. Grant on the item's source scope; this does not grant raw source export"
            },
            {
                action: 'approve',
                label: 'Approve items',
                description: 'Sign off deliverables, approve studio assets and change requests'
            },
            {
                action: 'publish',
                label: 'Publish audit reports',
                description: 'Release a completed audit to the program record'
            },
            {
                action: 'close',
                label: 'Close & delete programs',
                description: 'Close, archive or delete a program, delete any document, and delete CPARS/QASP scores — terminal and destructive'
            },
            {
                action: 'correct_closeout',
                label: 'Correct a closed period closeout',
                description: "Change a closed contract period's closeout answers — logged as a correction. Closing the period still needs Close."
            },
            {
                action: 'activate',
                label: 'Activate programs',
                description: 'Flip the go-live activation gate on a staged program'
            },
            {
                action: 'create_program',
                label: 'Create programs',
                description: 'Mint a new program under the business unit or org node this role is granted on — Delivery VP work'
            },
            {
                action: 'manage_members',
                label: 'Staff program teams',
                description: "Add or remove people on a program's Roles section — the program lead staffs their team; a Delivery VP names the lead"
            },
            {
                action: 'manage_contacts',
                label: 'Remove program contacts',
                description: "Remove a contact (government or partner POC) from a program's Contacts — program-manager work"
            },
            {
                action: 'manage_self_assessment',
                label: 'Run the self-assessment lifecycle',
                description: "Create, edit, and complete a program's self-assessment (Audits) and send corrections — the Program Lead's readiness check, distinct from the formal Quality audit lane"
            }
        ]
    },
    {
        group: 'Contracts & awards',
        items: [
            {
                action: 'import',
                label: 'Import award packages',
                description: 'Bring an award document set into the program'
            },
            {
                action: 'begin_comparison',
                label: 'Run award/mod comparisons',
                description: 'Start a side-by-side comparison of an award or modification'
            },
            {
                action: 'commit',
                label: 'Apply contract modifications',
                description: 'Commit a reviewed change set so the mod takes effect'
            }
        ]
    },
    {
        group: 'Financials & quality',
        items: [
            {
                action: 'manage_quality',
                label: 'Run the audit lifecycle',
                description: 'Work audits and corrective actions, and delete CPARS/QASP scores'
            },
            {
                action: 'close_audit',
                label: 'Close audits',
                description: 'Close a published quality audit once its corrective actions are resolved — needs the audit lifecycle too, and nothing else that Close & delete programs allows'
            },
            {
                action: 'access_audit:read',
                label: 'See access history (organization-wide)',
                description: 'Who was granted what, by whom, and every access-request decision — across the WHOLE organization, wherever this role is granted. Admins and grantors have this implicitly'
            }
        ]
    },
    {
        // K2/WS3: every change needs a verb AND the matching work area. The
        // financial lane splits four ways so an EAC role can't touch invoicing.
        group: 'Where they can work',
        items: [
            {
                action: 'area:programs',
                label: 'Program setup & roster',
                description: 'Program records, roles, companies, contacts, capabilities'
            },
            {
                action: 'area:execution',
                label: 'Execution & schedule',
                description: 'Tasks, deliverables, periods of performance'
            },
            {
                action: 'area:execution.verdict',
                label: 'Execution — record the government verdict',
                description: "Recording the government's review of a submitted deliverable (approved, rejected, comments), apart from doing the work"
            },
            {
                action: 'area:quality',
                label: 'Quality & compliance',
                description: 'Risks, issues, audits, deviations'
            },
            {
                action: 'area:quality.scores',
                label: 'Quality — record CPARS & QASP scores',
                description: "Recording the government's CPARS and QASP scores, apart from other quality work — the VP of Delivery, the contracts team, and Quality Assurance (internal). Delete still needs close."
            },
            {
                action: 'area:growth',
                label: 'Growth & touchpoints',
                description: 'Customer touchpoints, growth opportunities'
            },
            {
                action: 'area:documents',
                label: 'Documents',
                description: 'Program documents and attachments'
            },
            {
                action: 'area:contracts',
                label: 'Contracts & mods',
                description: 'Contract changes, task orders, subcontracts'
            },
            {
                action: 'area:financials.eac',
                label: 'Financials — monthly health',
                description: "Recording a program's monthly financials (burn rate, AR) and its funding-alert threshold"
            },
            {
                action: 'area:financials.invoicing',
                label: 'Financials — invoicing & funding',
                description: 'Invoices and funding records'
            },
            {
                action: 'area:financials.pricing',
                label: 'Financials — pricing',
                description: 'CLIN and unit pricing'
            },
            {
                action: 'area:registries',
                label: 'Shared registries',
                description: 'Tenant-wide catalogs — company profiles, contract vehicles, capabilities — without needing any Delivery program access'
            }
        ]
    },
    {
        // SAD-948: company administration that is NOT access administration —
        // people, groups, roles and grants stay with tenant admins.
        group: 'Company administration',
        items: [
            {
                action: 'area:it_admin',
                label: 'IT Admin — sign-in configuration',
                description: "The organization's single sign-on and SharePoint app settings. Grant on the organization"
            },
            {
                action: 'area:company_settings',
                label: 'Company settings',
                description: 'Org structure, government organizations, audit templates, notification delivery status and usage. Grant on the organization'
            }
        ]
    }
];
const KNOWN_ACTIONS = new Set([
    'read',
    ...CAPABILITY_GROUPS.flatMap((g)=>g.items.map((i)=>i.action))
]);
export function actionLabel(action) {
    for (const g of CAPABILITY_GROUPS){
        const hit = g.items.find((i)=>i.action === action);
        if (hit) return hit.label;
    }
    if (action === 'read') return 'See content';
    return action;
}
// Verbs present in the catalog that this page has no translation for — they
// render raw in an "Other" group rather than disappearing.
export function unknownActions(bundles) {
    const set = new Set();
    for (const b of bundles){
        for (const a of b.permittedActions)if (!KNOWN_ACTIONS.has(a)) set.add(a);
        for (const a of b.requiresBreakGlassFor)if (!KNOWN_ACTIONS.has(a)) set.add(a);
    }
    return [
        ...set
    ].sort();
}
